// ✅ SKILL FACTURXPRO: Architecture 3 couches
// Component (UI only) → Custom Hook (Logic) → API Layer (HTTP)

import {
  AlertCircle,
  Bell,
  Calendar,
  CheckCircle,
  Copy,
  Download,
  Edit,
  EuroIcon,
  Eye,
  FileEdit,
  FileText,
  MoreVertical,
  Plus,
  Send,
  Trash2,
  Wrench,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { confirm } from "../../../stores/confirmStore";
import { useQuotesManagement } from "../../../hooks/useQuotesManagement";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FilterBar,
  FilterOption,
} from '@/components/ui';

import { Button, Select } from '@/components/ui-v2';
import { downloadCSV, formatDateForExport, formatCurrencyForExport, type ExportColumn } from '@/utils/exportUtils';
import { DepositInvoiceModal } from './DepositInvoiceModal';
import { quotesAPI } from '@/utils/api';
import { notify } from '@/stores/notificationStore';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const QuotesListResponsive: React.FC = () => {
  // ✅ SKILL FACTURXPRO: Logique extraite vers custom hook
  const {
    // Data
    quotes,
    filteredQuotes,
    loading,
    activeMenu,
    // Filter State
    searchQuery,
    activeStatuses,
    dateFrom,
    dateTo,
    // Actions
    deleteQuote,
    duplicateQuote,
    updateQuoteStatus,
    createInvoiceFromQuote,
    sendReminderForQuote,
    getStatusOptions,
    getStatusConfig,
    formatDate,
    formatCurrency,
    // UI Actions
    setActiveMenu,
    setSearchQuery,
    setActiveStatuses,
    setDateFrom,
    setDateTo,
  } = useQuotesManagement();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Deposit invoice modal state
  const [depositModalQuote, setDepositModalQuote] = useState<{ id: string; number: string; totalTTC: number } | null>(null);

  const depositMutation = useMutation({
    mutationFn: (params: { quoteId: string; data: { depositType: 'PERCENT' | 'FIXED'; depositValue: number; issueDate: string; dueDate: string } }) =>
      quotesAPI.createDepositInvoice(params.quoteId, params.data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['deposit-summary'] });
      setDepositModalQuote(null);
      if (result?.id) navigate(`/invoices/${result.id}`);
    },
    onError: (err) => {
      notify.error(
        err instanceof Error ? err.message : t('invoices.deposit.createError', 'Erreur lors de la création de l\'acompte'),
        'Erreur acompte'
      );
    },
  });

  const finalInvoiceMutation = useMutation({
    mutationFn: (params: { quoteId: string; data: { issueDate: string; dueDate: string } }) =>
      quotesAPI.createFinalInvoice(params.quoteId, params.data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['deposit-summary'] });
      if (result?.id) navigate(`/invoices/${result.id}`);
    },
    onError: (err) => {
      notify.error(
        err instanceof Error ? err.message : t('invoices.deposit.finalError', 'Erreur lors de la création de la facture de solde'),
        'Erreur facture de solde'
      );
    },
  });

  const handleCreateFinalInvoice = async (quote: any) => {
    if (quote.status !== 'ACCEPTED') return;
    const confirmed = await confirm({
      title: t('quotes.list.generateFinalInvoice', 'Facture de solde'),
      message: t('quotes.list.generateFinalInvoiceMessage', 'Créer la facture de solde pour le devis {{number}} ? Les acomptes seront déduits.', { number: quote.number }),
      confirmText: t('common.generate', 'Générer'),
      cancelText: t('common.actions.cancel'),
      variant: 'info',
    });
    if (!confirmed) return;

    const today = new Date().toISOString().split('T')[0];
    const dueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    finalInvoiceMutation.mutate({
      quoteId: quote.id.toString(),
      data: { issueDate: today, dueDate },
    });
  };

  // ✅ SKILL FACTURXPRO: Handlers simplifiés (UI only - confirmation dialogs + navigation)
  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: t('quotes.list.deleteTitle'),
      message: t('quotes.list.deleteMessage'),
      confirmText: t('common.actions.delete'),
      cancelText: t('common.actions.cancel'),
      variant: "danger",
    });
    if (!confirmed) return;

    await deleteQuote(id);
  };

  const handleDuplicate = async (quote: any) => {
    const confirmed = await confirm({
      title: t('quotes.list.duplicateTitle'),
      message: t('quotes.list.duplicateMessage', { number: quote.number }),
      confirmText: t('common.actions.duplicate'),
      cancelText: t('common.actions.cancel'),
      variant: "info",
    });
    if (!confirmed) return;

    const newQuote = await duplicateQuote(quote.id.toString());
    if (newQuote && newQuote.id) {
      navigate(`/quotes/${newQuote.id}/edit`);
    }
  };

  const handleCreateInvoice = async (quote: any) => {
    // ✅ Vérification du statut avant l'appel API
    if (quote.status !== 'ACCEPTED') {
      await confirm({
        title: t('quotes.list.quoteNotAccepted'),
        message: t('quotes.list.quoteNotAcceptedInvoice', { number: quote.number, status: quote.status }),
        confirmText: t('common.ok'),
        cancelText: "",
        variant: "warning",
      });
      return;
    }

    const confirmed = await confirm({
      title: t('quotes.list.generateInvoice'),
      message: t('quotes.list.generateInvoiceMessage', { number: quote.number }),
      confirmText: t('common.generate'),
      cancelText: t('common.actions.cancel'),
      variant: "info",
    });
    if (!confirmed) return;

    const result = await createInvoiceFromQuote(quote.id.toString());
    if (result && result.id) {
      navigate(`/invoices/${result.id}`);
    }
  };

  const handleStatusChange = async (quoteId: string, newStatus: string) => {
    await updateQuoteStatus(quoteId, newStatus);
  };

  const handleSendReminder = async (quote: any) => {
    const confirmed = await confirm({
      title: t('quotes.list.sendReminderTitle'),
      message: t('quotes.list.sendReminderMessage', { number: quote.number, email: quote.Client?.email || t('common.client') }),
      confirmText: t('common.actions.send'),
      cancelText: t('common.actions.cancel'),
      variant: "info",
    });
    if (!confirmed) return;

    await sendReminderForQuote(quote.id);
  };

  // ✅ CRM ARTISANS: Créer une intervention depuis un devis accepté
  const handleCreateWorkOrder = async (quote: any) => {
    // Vérification du statut - uniquement pour devis acceptés
    if (quote.status !== 'ACCEPTED') {
      await confirm({
        title: t('quotes.list.quoteNotAccepted'),
        message: t('quotes.list.quoteNotAcceptedWorkOrder', { number: quote.number, status: quote.status }),
        confirmText: t('common.ok'),
        cancelText: "",
        variant: "warning",
      });
      return;
    }

    const confirmed = await confirm({
      title: t('quotes.list.createWorkOrder'),
      message: t('quotes.list.createWorkOrderMessage', { number: quote.number, client: quote.Client?.fullName || quote.Client?.companyName || t('common.client') }),
      confirmText: t('quotes.list.createWorkOrderConfirm'),
      cancelText: t('common.actions.cancel'),
      variant: "info",
    });
    if (!confirmed) return;

    // Navigation vers la création d'intervention avec le quoteId pré-rempli
    navigate(`/work-orders?action=new&quoteId=${quote.id}&clientId=${quote.clientId}`);
  };

  // ✅ SKILL FACTURXPRO: Computed values spécifiques à l'UI (status counts pour les filtres)
  const statusCounts = useMemo(() => {
    return quotes.reduce((acc, quote) => {
      const status = quote.status || "DRAFT";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [quotes]);

  // Options de filtres par statut
  const statusFilterOptions: FilterOption[] = [
    {
      value: "draft",
      label: t('quotes.status.DRAFT'),
      icon: FileEdit,
      color: "gray",
      count: statusCounts.draft || 0,
    },
    {
      value: "sent",
      label: t('quotes.status.SENT'),
      icon: Send,
      color: "blue",
      count: statusCounts.sent || 0,
    },
    {
      value: "accepted",
      label: t('quotes.status.ACCEPTED'),
      icon: CheckCircle,
      color: "green",
      count: statusCounts.accepted || 0,
    },
    {
      value: "rejected",
      label: t('quotes.status.REJECTED'),
      icon: XCircle,
      color: "red",
      count: statusCounts.rejected || 0,
    },
    {
      value: "expired",
      label: t('quotes.status.EXPIRED'),
      icon: AlertCircle,
      color: "orange",
      count: statusCounts.expired || 0,
    },
  ];

  // Helper pour obtenir l'icône selon le statut
  const getStatusIcon = (status: string) => {
    const statusMap: Record<string, any> = {
      DRAFT: FileEdit,
      SENT: Send,
      ACCEPTED: CheckCircle,
      REJECTED: XCircle,
      EXPIRED: AlertCircle,
    };
    return statusMap[status] || FileEdit;
  };

  // Handlers pour les filtres
  const handleStatusToggle = (status: string) => {
    const newStatuses = activeStatuses.includes(status)
      ? activeStatuses.filter((s) => s !== status)
      : [...activeStatuses, status];
    setActiveStatuses(newStatuses);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setActiveStatuses([]);
    setDateFrom("");
    setDateTo("");
  };

  // ✅ Sprint 4 - Export CSV
  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      DRAFT: t('quotes.status.DRAFT'),
      SENT: t('quotes.status.SENT'),
      ACCEPTED: t('quotes.status.ACCEPTED'),
      REJECTED: t('quotes.status.REJECTED'),
      EXPIRED: t('quotes.status.EXPIRED'),
    };
    return labels[status] || status;
  };

  const handleExportCSV = () => {
    const columns: ExportColumn<typeof filteredQuotes[0]>[] = [
      { key: 'number', header: t('quotes.list.tableNumber') },
      {
        key: 'Client.companyName',
        header: t('quotes.list.tableClient'),
        formatter: (_, row) => row.Client?.companyName || row.Client?.fullName || '',
      },
      {
        key: 'status',
        header: t('quotes.list.tableStatus'),
        formatter: (value) => getStatusLabel(value as string),
      },
      {
        key: 'issueDate',
        header: t('quotes.list.tableDate'),
        formatter: (value) => formatDateForExport(value as string),
      },
      {
        key: 'validUntil',
        header: t('quotes.list.tableValidUntil'),
        formatter: (value) => formatDateForExport(value as string),
      },
      {
        key: 'totalHT',
        header: t('quotes.list.tableAmountHT'),
        formatter: (value) => formatCurrencyForExport(value as number),
      },
      {
        key: 'totalTVA',
        header: t('templates.common.vat'),
        formatter: (value) => formatCurrencyForExport(value as number),
      },
      {
        key: 'totalTTC',
        header: t('quotes.list.tableAmountTTC'),
        formatter: (value) => formatCurrencyForExport(value as number),
      },
    ];

    const today = new Date().toISOString().split('T')[0];
    downloadCSV(filteredQuotes, columns, {
      filename: `devis_export_${today}`,
      format: 'csv',
    });
  };

  return (
    <div className="px-8 pt-8 pb-12 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900">
            {t('quotes.title')}
          </h2>
          <p className="text-base text-gray-500 font-light tracking-wide mt-2">
            {filteredQuotes.length === quotes.length
              ? t('quotes.list.totalCount', { count: quotes.length })
              : t('quotes.list.filteredCount', { filtered: filteredQuotes.length, total: quotes.length })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Export CSV Button */}
          {filteredQuotes.length > 0 && (
            <Button
              variant="outline"
              onClick={handleExportCSV}
              icon={<Download className="w-5 h-5" />}
            >
              {t('common.exportCSV')}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => navigate("/quotes/new")}
            icon={<Plus className="w-5 h-5" />}
          >
            {t('quotes.create')}
          </Button>
        </div>
      </div>

      {/* Filter Bar - Always visible when not loading */}
      {!loading && (
        <FilterBar
          searchPlaceholder={t('common.searchByNumberClientAmount')}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          statusFilters={statusFilterOptions}
          activeStatuses={activeStatuses}
          onStatusToggle={handleStatusToggle}
          showDateFilter={true}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onClearFilters={handleClearFilters}
          totalItems={quotes.length}
          filteredItems={filteredQuotes.length}
        />
      )}

      {/* Main Content Container */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 overflow-hidden">
        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">{t('quotes.list.loading')}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && quotes.length === 0 && (
          <div className="text-center py-16 px-8">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('quotes.list.empty')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('quotes.list.emptyDescription')}
            </p>
            <div className="flex justify-center">
              <Button
                onClick={() => navigate("/quotes/new")}
                icon={<Plus className="w-5 h-5" />}
              >
                {t('quotes.list.createFirst')}
              </Button>
            </div>
          </div>
        )}

        {/* No results after filtering */}
        {!loading && quotes.length > 0 && filteredQuotes.length === 0 && (
          <div className="text-center py-16 px-8">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('quotes.list.noResults')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('quotes.list.emptyFilter')}
            </p>
            <Button onClick={handleClearFilters} variant="outline">
              {t('common.clearFilters')}
            </Button>
          </div>
        )}

        {/* Quotes Grid - Mobile Cards / Desktop Table */}
        {!loading && filteredQuotes.length > 0 && (
          <>
          {/* Mobile Card View */}
          <div className="grid grid-cols-1 gap-4 md:hidden p-6">
            {filteredQuotes.map((quote) => {
              const statusConfig = getStatusConfig(quote.status || "DRAFT");
              const StatusIcon = statusConfig.icon;

              return (
                <Card key={quote.id} padding="none" className="overflow-hidden bg-white/20 backdrop-blur-xl border border-white/30 hover:bg-white/35 hover:shadow-2xl transition-all duration-300">
                  <CardHeader className="bg-white/10 backdrop-blur-sm p-4 border-b border-white/20">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg font-bold text-gray-900 mb-1">
                          {quote.number}
                        </CardTitle>
                        <p className="text-sm text-gray-600 truncate">
                          {quote.Client?.fullName || quote.Client?.companyName || "-"}
                        </p>
                        {quote.Client?.companyName && quote.Client?.fullName && (
                          <p className="text-xs text-gray-500 truncate">
                            {quote.Client?.companyName}
                          </p>
                        )}
                      </div>
                      <div className="relative">
                        <button
                          onClick={() =>
                            setActiveMenu(
                              activeMenu === quote.id ? null : quote.id
                            )
                          }
                          className="p-2 hover:bg-white/30 hover:backdrop-blur-sm rounded-lg transition-all duration-300"
                        >
                          <MoreVertical className="w-5 h-5 text-gray-600" />
                        </button>

                        {/* Actions menu */}
                        {activeMenu === quote.id && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/30 py-1 z-10">
                            <button
                              onClick={() => {
                                navigate(`/quotes/${quote.id}`);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-teal-700 hover:bg-teal-50"
                            >
                              <Eye className="w-4 h-4" />
                              {t('quotes.list.view')}
                            </button>
                            <button
                              onClick={() => {
                                navigate(`/quotes/${quote.id}/edit`);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Edit className="w-4 h-4" />
                              {t('quotes.list.modify')}
                            </button>
                            <button
                              onClick={() => {
                                handleDuplicate(quote);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-purple-700 hover:bg-purple-50"
                            >
                              <Copy className="w-4 h-4" />
                              {t('common.actions.duplicate')}
                            </button>
                            <button
                              onClick={() => {
                                handleCreateInvoice(quote);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                            >
                              <FileText className="w-4 h-4" />
                              {t('quotes.list.generateInvoiceBtn')}
                            </button>
                            {quote.status === 'ACCEPTED' && (
                              <>
                                <button
                                  onClick={() => {
                                    setDepositModalQuote({
                                      id: quote.id,
                                      number: quote.number,
                                      totalTTC: Number(quote.totalTTC),
                                    });
                                    setActiveMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-amber-700 hover:bg-amber-50"
                                >
                                  <EuroIcon className="w-4 h-4" />
                                  {t('quotes.list.depositInvoiceBtn', "Facture d'acompte")}
                                </button>
                                <button
                                  onClick={() => {
                                    handleCreateFinalInvoice(quote);
                                    setActiveMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                                >
                                  <FileText className="w-4 h-4" />
                                  {t('quotes.list.finalInvoiceBtn', 'Facture de solde')}
                                </button>
                                <button
                                  onClick={() => {
                                    handleCreateWorkOrder(quote);
                                    setActiveMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-teal-700 hover:bg-teal-50"
                                >
                                  <Wrench className="w-4 h-4" />
                                  {t('quotes.list.createWorkOrder')}
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => {
                                handleSendReminder(quote);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-orange-700 hover:bg-orange-50"
                            >
                              <Bell className="w-4 h-4" />
                              {t('quotes.list.sendReminder')}
                            </button>
                            <button
                              onClick={() => {
                                handleDelete(quote.id);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              {t('common.actions.delete')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="p-4 space-y-3">
                    {/* Date */}
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">
                        {formatDate(quote.issueDate instanceof Date ? quote.issueDate.toISOString() : quote.issueDate)}
                      </span>
                    </div>

                    {/* Amount */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <EuroIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{t('quotes.list.tableAmountTTC')}</span>
                      </div>
                      <span className="text-lg font-bold text-gray-900">
                        {formatCurrency(quote.totalTTC)}
                      </span>
                    </div>

                    {/* Status */}
                    <div>
                      <Select
                        value={quote.status || "DRAFT"}
                        onChange={(e) =>
                          handleStatusChange(quote.id, e.target.value)
                        }
                        size="sm"
                        variant="chip"
                        fullWidth={true}
                        icon={getStatusIcon(quote.status || "DRAFT")}
                      >
                        {getStatusOptions(quote.status || "DRAFT").map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/20">
                <thead className="bg-white/10 backdrop-blur-sm">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('quotes.list.tableNumber')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('quotes.list.tableClient')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('quotes.list.tableDate')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('quotes.list.tableAmountTTC')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('quotes.list.tableStatus')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('quotes.list.tableActions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  {filteredQuotes.map((quote) => {
                    const statusConfig = getStatusConfig(
                      quote.status || "DRAFT"
                    );
                    const StatusIcon = statusConfig.icon;

                    return (
                      <tr
                        key={quote.id}
                        className="hover:bg-white/10 transition-all duration-300"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {quote.number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {quote.Client?.fullName || quote.Client?.companyName || "-"}
                            </div>
                            {quote.Client?.companyName && quote.Client?.fullName && (
                              <div className="text-sm text-gray-500">
                                {quote.Client?.companyName}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(quote.issueDate instanceof Date ? quote.issueDate.toISOString() : quote.issueDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                          {formatCurrency(quote.totalTTC)}
                        </td>
                        <td className="px-6 py-4">
                          <Select
                            value={quote.status || "DRAFT"}
                            onChange={(e) =>
                              handleStatusChange(quote.id, e.target.value)
                            }
                            size="sm"
                            variant="chip"
                            fullWidth={false}
                            icon={getStatusIcon(quote.status || "DRAFT")}
                          >
                            {getStatusOptions(quote.status || "DRAFT").map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/quotes/${quote.id}`)}
                              className="p-2 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors"
                              title={t('quotes.list.view')}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                navigate(`/quotes/${quote.id}/edit`)
                              }
                              className="p-2 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors"
                              title={t('quotes.list.modify')}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDuplicate(quote)}
                              className="p-2 text-purple-600 hover:text-purple-900 hover:bg-purple-50 rounded-lg transition-colors"
                              title={t('common.actions.duplicate')}
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleCreateInvoice(quote)}
                              className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors"
                              title={t('quotes.list.generateInvoice')}
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            {quote.status === 'ACCEPTED' && (
                              <>
                                <button
                                  onClick={() => setDepositModalQuote({
                                    id: quote.id,
                                    number: quote.number,
                                    totalTTC: Number(quote.totalTTC),
                                  })}
                                  className="p-2 text-amber-600 hover:text-amber-900 hover:bg-amber-50 rounded-lg transition-colors"
                                  title={t('quotes.list.depositInvoiceBtn', "Facture d'acompte")}
                                >
                                  <EuroIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleCreateFinalInvoice(quote)}
                                  className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                                  title={t('quotes.list.finalInvoiceBtn', 'Facture de solde')}
                                >
                                  <FileText className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleCreateWorkOrder(quote)}
                                  className="p-2 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors"
                                  title={t('quotes.list.createWorkOrder')}
                                >
                                  <Wrench className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleSendReminder(quote)}
                              className="p-2 text-orange-600 hover:text-orange-900 hover:bg-orange-50 rounded-lg transition-colors"
                              title={t('quotes.list.sendReminder')}
                            >
                              <Bell className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(quote.id)}
                              className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                              title={t('common.actions.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          </>
        )}
      </div>

      {/* Deposit Invoice Modal */}
      {depositModalQuote && (
        <DepositInvoiceModal
          quoteId={depositModalQuote.id}
          quoteNumber={depositModalQuote.number}
          quoteTotalTTC={depositModalQuote.totalTTC}
          isOpen={true}
          onClose={() => setDepositModalQuote(null)}
          onSubmit={(data) => depositMutation.mutate({ quoteId: depositModalQuote.id, data })}
          isLoading={depositMutation.isPending}
        />
      )}
    </div>
  );
};
